import { useContext, useMemo } from 'react';
import { PushpinProps } from '../../../types/MapModels';
import { BusPositionHistoryContext } from '../../bushistory/BusHistoryContext';
import { COMPACT_VEHICLE_POPUP } from '../VehicleMapElements';
import AzureMapComponent from './AzureMapComponent';

function toCoordinatePair(lat: number, lon: number): [Latitude, Longitude] {
    return [lat, lon] as [Latitude, Longitude];
}

function getLocationCoordinates(source: any): [Latitude, Longitude] | null {
    const location = source?.getLocation ? source.getLocation() : source?.location;
    const lat = Number(location?.latitude);
    const lon = Number(location?.longitude);

    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        return null;
    }

    return [lat as Latitude, lon as Longitude];
}

function getPushpinTitle(source: any): string | undefined {
    if (source?.getTitle) {
        return source.getTitle();
    }

    return source?.options?.title;
}

function getPushpinColor(source: any): string | undefined {
    if (source?.getColor) {
        return source.getColor();
    }

    return source?.options?.color;
}

function getPolylineLocations(polyline: any): [Latitude, Longitude][] {
    if (polyline?.getLocations) {
        return polyline.getLocations().map((pos: any) => toCoordinatePair(pos.latitude, pos.longitude));
    }

    if (Array.isArray(polyline?.locations)) {
        return polyline.locations
            .map((pos: any) => {
                if (!Array.isArray(pos) || pos.length < 2) {
                    return null;
                }

                const lat = Number(pos[0]);
                const lon = Number(pos[1]);
                if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
                    return null;
                }

                return toCoordinatePair(lat, lon);
            })
            .filter((pos: [Latitude, Longitude] | null): pos is [Latitude, Longitude] => pos !== null);
    }

    return [];
}

function getPolylineStrokeColor(polyline: any): string {
    if (polyline?.getStrokeColor) {
        return polyline.getStrokeColor();
    }

    return polyline?.options?.strokeColor || '#248bd8';
}

function getPolylineStrokeThickness(polyline: any): number {
    if (polyline?.getStrokeThickness) {
        return polyline.getStrokeThickness();
    }

    const thickness = Number(polyline?.options?.strokeThickness);
    return Number.isFinite(thickness) ? thickness : 3;
}

const AzureMapFromContext = () => {
    const {
        routesState,
        directionsState,
        stopsState,
        displayDirectionStops,
        vehicles,
    } = useContext(BusPositionHistoryContext);

    const selectStopPushpin = (): PushpinProps[] => {
        if (stopsState.selectedStop === null) return [];
        const model = stopsState.selectedStop;
        const location = getLocationCoordinates(model.pushpin);
        if (!location) {
            return [];
        }

        return [{
            location,
            options: {
                title: getPushpinTitle(model.pushpin),
                color: getPushpinColor(model.pushpin),
                icon: 'stopSelectedIcon',
            },
            eventHandlers: [],
        }];
    };

    const selectRoutePushpin = (): PushpinProps[] => {
        if (directionsState.selectedDirection === null) return [];
        const expect = stopsState?.selectedStop ?? { stopId: 0 };
        return directionsState.selectedDirection?.getStops()
            .filter(x => x.stopId !== expect?.stopId)
            .map<PushpinProps | null>(model => {
                const location = getLocationCoordinates(model.pushpin);
                if (!location) {
                    return null;
                }

                return {
                    location,
                    options: {
                        title: getPushpinTitle(model.pushpin),
                        color: getPushpinColor(model.pushpin),
                        icon: 'stopIcon',
                    },
                    eventHandlers: [],
                };
            })
            .filter((model): model is PushpinProps => model !== null);
    };

    const selectVehiclesPushpin = (): PushpinProps[] => {
        if (vehicles === null) return [];
        const values = Object.values(vehicles);
        return values
            .map<PushpinProps | null>((model) => {
                const location = getLocationCoordinates(model.infobox);
                if (!location) {
                    return null;
                }

                return {
                    location,
                    options: {
                        icon: model.highlighted ? 'busRedWhite' : 'busBlue',
                    },
                    eventHandlers: [],
                };
            })
            .filter((model): model is PushpinProps => model !== null);
    };

    const pushpins: PushpinProps[] = useMemo(() => {
        let result: PushpinProps[] = [];
        if (stopsState.selectedStop !== null && !displayDirectionStops) {
            result = result.concat(selectStopPushpin());
        } else if (stopsState.stops !== null && displayDirectionStops && directionsState.selectedDirection) {
            result = result.concat(selectRoutePushpin())
                .concat(selectStopPushpin());
        }

        return result;
    }, [stopsState.selectedStop, stopsState.stops, displayDirectionStops, directionsState.selectedDirection]);

    const vehiclesData: PushpinProps[] = useMemo(() => {
        let result: PushpinProps[] = [];
        if(vehicles !== null){
            result = result.concat(selectVehiclesPushpin());
        }
        return result;
    }, [vehicles]);

    const polylines = useMemo(() => {
        if (routesState.selectedRoute === null || !directionsState?.directions) return [];

        if (directionsState.selectedDirection !== null) {
            const locations = getPolylineLocations(directionsState.selectedDirection.polyline);

            return [{
                locations,
                options: {
                    strokeColor: getPolylineStrokeColor(directionsState.selectedDirection.polyline),
                    strokeThickness: getPolylineStrokeThickness(directionsState.selectedDirection.polyline),
                },
                eventHandlers: [],
            }];
        } else {
            const locations = directionsState.directions.flatMap(d =>
                getPolylineLocations(d.polyline),
            );

            return [{
                locations,
                options: {
                    strokeColor: '#248bd8',
                    strokeThickness: 3,
                },
                eventHandlers: [],
            }];
        }

    }, [routesState.selectedRoute, directionsState.directions, directionsState.selectedDirection]);

    const infoboxes = useMemo(() => {
        if (!vehicles) return [];
        const values = Object.values(vehicles);
        return values
            .map<{
                location: [Latitude, Longitude];
                text: string;
                options: {
                    showCloseButton: boolean;
                    offset: [number, number];
                    minWidth: number;
                    padding: string;
                };
            } | null>((model) => {
                const location = getLocationCoordinates(model.infobox);
                if (!location) {
                    return null;
                }

                return {
                    location,
                    text: `${model.id} ${model.description}`,
                    options: {
                        showCloseButton: true,
                        offset: COMPACT_VEHICLE_POPUP.offset,
                        minWidth: COMPACT_VEHICLE_POPUP.minWidth,
                        padding: COMPACT_VEHICLE_POPUP.padding,
                    },
                };
            })
            .filter((model): model is {
                location: [Latitude, Longitude];
                text: string;
                options: {
                    showCloseButton: boolean;
                    offset: [number, number];
                    minWidth: number;
                    padding: string;
                };
            } => model !== null);
    }, [vehicles]);

    return <AzureMapComponent pushpinsProp={pushpins}
                              polylinesProp={polylines}
                              infoboxesProp={infoboxes}
                              vehiclesProp={vehiclesData}
    />;
};

export default AzureMapFromContext;
